import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus, X, Loader2, MapPin, Camera, FolderKanban, ArrowUpRight, Building2,
  Copy, Check, RefreshCw, KeyRound, Users, Smartphone, LocateFixed, Plug, Database, CheckCircle2, AlertCircle, QrCode, FileSpreadsheet,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { claimTitle } from "@/lib/utils";
import { AlertsPanel, SwmsPanel } from "./AlertsSwmsPanels";

const Mono = ({ children, className = "", ...rest }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`} {...rest}>{children}</span>
);

const REGIONS = ["NSW", "QLD"];
const ASSESS = { NSW: { a: "NSWRA", p: "SmartyGrants" }, QLD: { a: "QRA", p: "MARS" } };
const WORKFLOW_CHIP = {
  draft: "border-white/20 text-white/45",
  quality_check: "border-amber-400/40 text-amber-300",
  ready: "border-sky-400/40 text-sky-300",
  submitted: "border-gold-500/40 text-gold-300",
  approved: "border-emerald-400/40 text-emerald-300",
};
const EMPTY = { name: "", council: "", event_name: "", event_date: "", region: "NSW", agrn: "", description: "", latitude: "", longitude: "" };

export default function FieldClaim() {
  const [claims, setClaims] = useState([]);
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState({ councils: [], agrn_activations: [], lga_regions: {} });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [locating, setLocating] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [t1Open, setT1Open] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/fieldclaim/claims").then((r) => setClaims(r.data)).catch(() => {});
  const loadOrg = () => api.get("/fieldclaim/org").then((r) => setOrg(r.data)).catch(() => {});
  useEffect(() => { load(); loadOrg(); api.get("/fieldclaim/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const norm = (s) => (s || "").toLowerCase().replace(/\b(regional|shire|city|municipal|council|the)\b/g, "").replace(/[^a-z ]/g, "").replace(/\s+/g, " ").trim();
  const cetRegion = useMemo(() => {
    if (!form.council || !meta.lga_regions) return null;
    if (meta.lga_regions[form.council]) return meta.lga_regions[form.council];
    const key = norm(form.council);
    const hit = Object.entries(meta.lga_regions).find(([k]) => norm(k) === key);
    return hit ? hit[1] : null;
  }, [form.council, meta.lga_regions]);

  const onAgrn = (v) => {
    const act = (meta.agrn_activations || []).find((a) => a.agrn === v || a.name === v);
    setForm((f) => act
      ? { ...f, agrn: act.agrn, event_name: f.event_name || act.name, event_date: f.event_date || act.start_date,
          council: f.council || (act.lgas?.length === 1 ? act.lgas[0] : f.council) }
      : { ...f, agrn: v });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, latitude: form.latitude === "" ? null : Number(form.latitude), longitude: form.longitude === "" ? null : Number(form.longitude) };
    try {
      const { data } = await api.post("/fieldclaim/claims", payload);
      setOpen(false); setForm(EMPTY);
      nav(`/dashboard/fieldclaim/${data.id}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8 text-white" data-testid="fieldclaim-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">FieldClaim by MDAu · field.claims</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Field evidence &amp; DRFA claims</h1>
          <p className="text-white/55 mt-3 font-light max-w-2xl">
            Create the disaster event here, then field crews connect their mobile devices with your
            team code and upload GPS-tagged, audit-ready damage evidence straight into the same claim.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/field" target="_blank" rel="noreferrer" data-testid="open-field-app-btn" className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-5 py-3 rounded-sm hover:bg-gold-500/10 transition-colors">
            <Smartphone className="h-4 w-4" /> Open field app
          </a>
          <button onClick={() => setOpen(true)} data-testid="new-claim-btn" className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2">
            <Plus className="h-4 w-4" /> New event
          </button>
        </div>
      </div>

      {org && <TeamPanel org={org} onRefresh={() => { loadOrg(); load(); }} onJoin={() => setJoinOpen(true)} onIntegrations={() => setT1Open(true)} />}

      <div className="grid lg:grid-cols-2 gap-4">
        <AlertsPanel />
        <SwmsPanel />
      </div>

      {claims.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light" data-testid="fieldclaim-empty">
          No events yet. Create your first disaster event to start capturing field evidence.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {claims.map((c) => (
            <button
              key={c.id}
              onClick={() => nav(`/dashboard/fieldclaim/${c.id}`)}
              data-testid={`claim-card-${c.id}`}
              className="group text-left border border-gold-500/25 bg-navy-900/40 p-6 hover:bg-navy-800 transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <Mono className="text-[0.55rem] text-gold-500/70">{c.assessing_body || c.region} · {c.submission_portal || "—"}</Mono>
                <ArrowUpRight className="h-4 w-4 text-white/25 group-hover:text-gold-400 transition-colors" />
              </div>
              <h3 className="font-heading text-xl leading-snug">{claimTitle(c)}</h3>
              <div className="flex items-center gap-4 mt-5 pt-4 border-t border-gold-500/15">
                <span data-testid={`claim-workflow-${c.id}`} className={`font-mono uppercase tracking-widest text-[0.5rem] border px-1.5 py-0.5 ${WORKFLOW_CHIP[c.workflow_status || "draft"]}`}>
                  {(c.workflow_status || "draft").replace("_", " ")}
                </span>
                <span className="flex items-center gap-1.5 text-white/60 text-xs"><FolderKanban className="h-3.5 w-3.5 text-gold-500/60" /> {c.asset_count} assets</span>
                <span className="flex items-center gap-1.5 text-white/60 text-xs"><Camera className="h-3.5 w-3.5 text-gold-500/60" /> {c.photo_count} photos</span>
                {c.latitude != null && c.longitude != null && (
                  <span className="flex items-center gap-1.5 text-emerald-300/80 text-xs" data-testid={`claim-located-${c.id}`}><MapPin className="h-3.5 w-3.5" /> Located</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-8 space-y-5 max-h-[90vh] overflow-y-auto" data-testid="claim-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-white">New disaster event</h2>
              <button type="button" onClick={() => setOpen(false)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <Field label="Event / claim name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="claim-name" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Council / LGA</Mono>
                <input list="council-list" value={form.council} onChange={(e) => setForm((f) => ({ ...f, council: e.target.value }))} data-testid="claim-council" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500" />
                <datalist id="council-list">{(meta.councils || []).map((c) => <option key={c} value={c} />)}</datalist>
                {cetRegion && <Mono className="text-[0.5rem] text-gold-400/70 mt-2 block" data-testid="claim-cet-region-hint">CET region: {cetRegion}</Mono>}
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Region</Mono>
                <select value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} data-testid="claim-region" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500">
                  {REGIONS.map((r) => <option key={r} value={r} className="bg-navy-900">{r}</option>)}
                </select>
                <Mono className="text-[0.5rem] text-gold-400/70 mt-2 block" data-testid="claim-assessing-hint">Assessed by {ASSESS[form.region].a} · via {ASSESS[form.region].p}</Mono>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Event / disaster name" value={form.event_name} onChange={(v) => setForm((f) => ({ ...f, event_name: v }))} required={false} testid="claim-event" />
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Event date</Mono>
                <input type="date" value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} data-testid="claim-eventdate" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
            </div>
            <div>
              <Mono className="text-white/60 text-[0.55rem] block mb-2">AGRN / activation reference</Mono>
              <input list="agrn-list" value={form.agrn} onChange={(e) => onAgrn(e.target.value)} data-testid="claim-agrn" placeholder="Select or type an AGRN" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500" />
              <datalist id="agrn-list">{(meta.agrn_activations || []).map((a) => <option key={a.agrn} value={a.agrn}>{a.name}</option>)}</datalist>
              <Mono className="text-[0.5rem] text-white/40 mt-2 block">Selecting a known activation auto-fills the event name &amp; date.</Mono>
            </div>

            <div className="border border-gold-500/20 bg-navy-950/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Mono className="text-gold-400/80 text-[0.55rem] flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Event location point</Mono>
                <button type="button" onClick={useMyLocation} disabled={locating} data-testid="claim-use-location" className="inline-flex items-center gap-1.5 text-gold-300 hover:text-gold-200 text-xs disabled:opacity-60">
                  {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Use my location
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Latitude" value={form.latitude} onChange={(v) => setForm((f) => ({ ...f, latitude: v }))} required={false} testid="claim-lat" />
                <Field label="Longitude" value={form.longitude} onChange={(v) => setForm((f) => ({ ...f, longitude: v }))} required={false} testid="claim-lng" />
              </div>
              <p className="text-white/40 text-[0.65rem] font-light">Sets the map pin crews navigate to. Optional — can also be set later in the field.</p>
            </div>

            <div>
              <Mono className="text-white/60 text-[0.55rem] block mb-2">Description</Mono>
              <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="claim-description" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="save-claim-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Create event
            </button>
          </form>
        </div>
      )}

      {joinOpen && <JoinTeamModal onClose={() => setJoinOpen(false)} onJoined={() => { setJoinOpen(false); loadOrg(); load(); }} />}
      {t1Open && <T1ConfigModal isOwner={org?.is_owner} onClose={() => setT1Open(false)} />}
    </div>
  );
}

function T1ConfigModal({ isOwner, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ base_url: "", client_id: "", client_secret: "", scope: "", environment: "Sandbox", enabled: false });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/fieldclaim/integrations/t1").then((r) => {
      setCfg(r.data);
      setForm((f) => ({ ...f, base_url: r.data.base_url || "", client_id: r.data.client_id || "", scope: r.data.scope || "", environment: r.data.environment || "Sandbox", enabled: !!r.data.enabled }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true); setResult(null);
    const payload = { ...form };
    if (!payload.client_secret) delete payload.client_secret; // keep existing secret if untouched
    try { const { data } = await api.put("/fieldclaim/integrations/t1", payload); setCfg(data); setForm((f) => ({ ...f, client_secret: "" })); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true); setResult(null);
    try { const { data } = await api.post("/fieldclaim/integrations/t1/test"); setResult(data); }
    catch (e) { setResult({ ok: false, message: e?.response?.data?.detail || "Test failed" }); }
    finally { setTesting(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-8 space-y-5 max-h-[92vh] overflow-y-auto" data-testid="t1-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-gold-400" />
            <h2 className="font-heading text-xl text-white">TechnologyOne connector</h2>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <p className="text-white/55 text-sm font-light">
          Connect your council's OneCouncil (Ci Anywhere) tenant to pull the asset register and post approved DRFA
          costs to Financials. {cfg?.configured
            ? <span className="text-emerald-300">Connector is configured.</span>
            : <span className="text-amber-300">Not configured — manual entry &amp; CSV import remain available as fallback.</span>}
        </p>

        {!isOwner ? (
          <div className="border border-gold-500/20 bg-navy-950/60 p-4 text-white/60 text-sm" data-testid="t1-owner-only">Only the team owner can manage integrations.</div>
        ) : (
          <>
            <label className="block"><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Base URL</Mono>
              <input value={form.base_url} onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))} placeholder="https://<council>.t1cloud.com/ci" data-testid="t1-base-url" className={t1inp} /></label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block"><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Client ID</Mono>
                <input value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} data-testid="t1-client-id" className={t1inp} /></label>
              <label className="block"><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Client secret {cfg?.has_secret && <span className="text-emerald-400/70">· saved</span>}</Mono>
                <input type="password" value={form.client_secret} onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))} placeholder={cfg?.has_secret ? "•••••••• (unchanged)" : ""} data-testid="t1-client-secret" className={t1inp} /></label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block"><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Scope</Mono>
                <input value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} placeholder="eam financials" data-testid="t1-scope" className={t1inp} /></label>
              <label className="block"><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Environment</Mono>
                <select value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))} data-testid="t1-environment" className={t1inp + " cursor-pointer"}>
                  <option className="bg-navy-900">Sandbox</option><option className="bg-navy-900">Production</option>
                </select></label>
            </div>
            <button type="button" onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))} data-testid="t1-enabled" className="flex items-center gap-2.5">
              <span className={`h-5 w-9 rounded-full transition-colors relative ${form.enabled ? "bg-gold-500" : "bg-navy-800 border border-gold-500/20"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${form.enabled ? "left-4" : "left-0.5"}`} />
              </span>
              <span className="text-sm text-white/70">Enable connector</span>
            </button>

            {result && (
              <div className={`flex items-start gap-2 text-sm p-3 border ${result.ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`} data-testid="t1-test-result">
                {result.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                <span>{result.message}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={save} disabled={busy} data-testid="t1-save-btn" className="flex-1 bg-gold-500 text-navy-950 font-semibold py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
              <button onClick={test} disabled={testing} data-testid="t1-test-btn" className="flex-1 border border-gold-500/40 text-gold-300 py-3 hover:bg-gold-500/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test connection</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const t1inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500";

function TeamPanel({ org, onRefresh, onJoin, onIntegrations }) {
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(org.join_code); } catch { /* clipboard blocked */ }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };
  const rotate = async () => {
    if (!window.confirm("Regenerate the team code? Existing devices will need the new code to connect.")) return;
    setRotating(true);
    try { await api.post("/fieldclaim/org/rotate-code"); onRefresh(); } finally { setRotating(false); }
  };

  return (
    <div className="border border-gold-500/25 bg-navy-900/50 p-5 flex flex-col md:flex-row md:items-center gap-5 justify-between" data-testid="team-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 border border-gold-500/30 flex items-center justify-center text-gold-400 shrink-0"><Building2 className="h-5 w-5" /></div>
        <div>
          <div className="font-heading text-lg leading-tight" data-testid="team-name">{org.name}</div>
          <Mono className="text-[0.5rem] text-white/45 flex items-center gap-2 mt-0.5">
            {org.region} <span className="text-white/25">·</span> <Users className="h-3 w-3" /> {org.member_count} {org.member_count === 1 ? "member" : "members"}
          </Mono>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 border border-gold-500/25 bg-navy-950 px-3 py-2">
          <KeyRound className="h-3.5 w-3.5 text-gold-500/60" />
          <Mono className="text-[0.5rem] text-white/40">Team code</Mono>
          <span className="font-mono tracking-[0.3em] text-gold-300 text-lg" data-testid="team-code">{org.join_code || "——————"}</span>
          <button onClick={copy} data-testid="copy-team-code" title="Copy code" className="text-white/40 hover:text-gold-300 ml-1">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
          </button>
          {org.is_owner && (
            <button onClick={rotate} disabled={rotating} data-testid="rotate-team-code" title="Regenerate code" className="text-white/40 hover:text-gold-300">
              {rotating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </button>
          )}
        </div>
        <button onClick={() => setQrOpen(true)} data-testid="team-qr-btn" className="inline-flex items-center gap-1.5 border border-gold-500/30 text-gold-300 hover:bg-gold-500/10 px-3 py-2 text-xs transition-colors">
          <QrCode className="h-3.5 w-3.5" /> QR connect
        </button>
        <Link to="/dashboard/fieldclaim/ledger" data-testid="ledger-nav-btn" className="inline-flex items-center gap-1.5 border border-gold-500/30 text-gold-300 hover:bg-gold-500/10 px-3 py-2 text-xs transition-colors">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Ledger importer
        </Link>
        <button onClick={onJoin} data-testid="join-team-btn" className="inline-flex items-center gap-1.5 border border-gold-500/30 text-gold-300 hover:bg-gold-500/10 px-3 py-2 text-xs transition-colors">
          <Smartphone className="h-3.5 w-3.5" /> Connect / join a team
        </button>
        <button onClick={onIntegrations} data-testid="integrations-btn" className="inline-flex items-center gap-1.5 border border-gold-500/30 text-gold-300 hover:bg-gold-500/10 px-3 py-2 text-xs transition-colors">
          <Database className="h-3.5 w-3.5" /> TechnologyOne
        </button>
      </div>
      {qrOpen && <QrConnectModal org={org} onClose={() => setQrOpen(false)} />}
    </div>
  );
}

function QrConnectModal({ org, onClose }) {
  const url = `${window.location.origin}/field/connect?code=${org.join_code}`;
  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="team-qr-modal" className="bg-navy-900 border border-gold-500/30 w-full max-w-sm p-6 text-center space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-white">Scan to connect</h2>
          <button onClick={onClose} data-testid="team-qr-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <p className="text-white/50 text-xs font-light">
          Field crews scan this with their phone camera (or the field app's scanner) to join <span className="text-gold-300">{org.name}</span> — no typing required.
        </p>
        <div className="bg-white p-4 inline-block rounded-sm" data-testid="team-qr-code">
          <QRCodeSVG value={url} size={208} level="M" fgColor="#0B0D10" bgColor="#FFFFFF" />
        </div>
        <div className="font-mono tracking-[0.4em] text-gold-300 text-2xl" data-testid="team-qr-code-text">{org.join_code}</div>
        <Mono className="text-[0.5rem] text-white/35 block">Fallback: enter the 6-character code in the field app</Mono>
      </div>
    </div>
  );
}

function JoinTeamModal({ onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/fieldclaim/org/join", { code: code.trim().toUpperCase() });
      setMsg(data.already_member ? `Already connected to ${data.name}.` : `Connected to ${data.name}.`);
      setTimeout(onJoined, 900);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not join. Check the code.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-navy-900 border border-gold-500/30 w-full max-w-sm p-8 space-y-5" data-testid="join-team-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-white">Connect to a team</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <p className="text-white/55 text-sm font-light">Enter the 6-character team code shared by your council or lead assessor to connect this account to their disaster events.</p>
        <input
          value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123"
          data-testid="join-code-input" autoFocus
          className="w-full bg-navy-950 border border-gold-500/25 px-4 py-4 text-center font-mono tracking-[0.5em] text-2xl text-gold-300 focus:outline-none focus:border-gold-500 uppercase"
        />
        {err && <div className="text-red-300 text-sm" data-testid="join-error">{err}</div>}
        {msg && <div className="text-emerald-300 text-sm" data-testid="join-success">{msg}</div>}
        <button type="submit" disabled={busy || code.length < 6} data-testid="join-submit-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Connect
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, required = true }) {
  return (
    <div>
      <Mono className="text-white/60 text-[0.55rem] block mb-2">{label}</Mono>
      <input required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500" />
    </div>
  );
}
